import {
  FetchCacheItemSources,
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchTeamsCacheKey,
  FetchCacheTeamsFilters,
  FetchTeamsSort,
  FetchTeamsPage,
  FetchTeamsLimit,
  FetchTeamsGqlQuery
} from '../../teamsTypes';
import { FetchCacheTeamsQueryResponse } from '../../queries/fetchCacheTeams.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_TEAMS_FILTERS,
  INITIAL_TEAMS_LIMIT,
  INITIAL_TEAMS_PAGE,
  INITIAL_TEAMS_SORT
} from '../../teamsConstants';

interface TeamsWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface TeamsWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface TeamsDefaultOptions {
  action?: string;
  cacheKey: FetchTeamsCacheKey;
  query: FetchTeamsGqlQuery;
  initialFilters?: FetchCacheTeamsFilters;
  initialSort?: FetchTeamsSort;
  initialPage?: FetchTeamsPage;
  initialLimit?: FetchTeamsLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

type TeamsOptions = TeamsDefaultOptions &
  (TeamsWithPrefetchItemOptions | TeamsWithoutPrefetchItemOptions);

const scope = 'teams';

function useCachePaginatedTeams<FetchTeamsType = FetchCacheTeamsQueryResponse>({
  action,
  cacheKey,
  initialFilters = INITIAL_TEAMS_FILTERS,
  initialSort = INITIAL_TEAMS_SORT,
  initialPage = INITIAL_TEAMS_PAGE,
  initialLimit = INITIAL_TEAMS_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: TeamsOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchTeamsType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    fetchCacheItemQuery: fetchItemQuery,
    fetchCacheItemSource: FetchCacheItemSources.DB,
    options,
    source,
    trackTotalHits
  });

  return {
    teamsData: data,
    teams: items,
    teamsError: itemsError,
    teamsErrorMessage: itemsErrorMessage,
    teamsTotalCount: itemsTotalCount,
    teamsFetched: isFetched,
    teamsLoading: isLoading,
    teamsIsPlaceholderData: isPlaceholderData,
    teamsFilters: currentFilters,
    teamsSort: currentSort,
    teamsPage: currentPage,
    teamsLimit: currentLimit,
    updateTeamCache: updateItemCache,
    updateTeamsCache: updateItemsCache,
    filterTeams: filterItems,
    changeTeamsFilters: changeItemsFilters,
    clearTeamsFilters: clearItemsFilters,
    clearTeamsFiltersPersistInitial: clearItemsFiltersPersistInitial,
    sortTeams: sortItems,
    paginateTeams: paginateItems,
    limitTeams: limitItems,
    prefetchTeams: prefetchItems,
    prefetchTeam: prefetchItem
  };
}

export default useCachePaginatedTeams;
